import { ColumnDef } from '@tanstack/react-table'
import { useTranslations } from '@/hooks/useTranslations'
import { useMemo } from 'react'

import { UserDropdown } from '@/constants/user'
import { useLanguage } from '@/hooks'
import * as DateUtil from '@/utils/date'
import { formatNumber, formatTableNumber } from '@/utils/number'

import { BillRowActions } from './BillRowActions'
import { Bill } from '@/models/bill/bill'
import { StatusBadge } from '@/components/common/Badge/Status'
import { BILL_LOCATION_LABELS, type BillLocationValue } from '@/constants/bill-location'
import { BILL_SALE_MODE_LABELS_TH } from '@/constants/bill-sale-mode'

type UseDatatableHeadersProps = {
  search: string
  onClickBillDropdownEvent: (eventName: UserDropdown, data: Bill) => void
}

export function useDatatableHeaders({ search, onClickBillDropdownEvent }: UseDatatableHeadersProps) {
  const t = useTranslations('admin.bill_management.table')

  const { locale } = useLanguage()

  const columns = useMemo(() => {
    const columnsRender: (ColumnDef<Bill> & { sticky?: 'left' | 'right' })[] = [
      {
        id: 'no',
        header: t('no'),
        cell: (ctx) => (
          <div className="text-start">
            {formatTableNumber({
              index: ctx.row.index + 1,
              pageSize: ctx.table.getState().pagination.pageSize,
              pageIndex: ctx.table.getState().pagination.pageIndex,
            })}
          </div>
        ),
        size: 60,
        enableSorting: false,
        sticky: 'left',
      },
      {
        id: 'bill_date',
        header: t('bill_date'),
        cell: (ctx) => ctx.row.original?.bill_date ?? '-',
        size: 120,
        enableSorting: true,
        sticky: 'left',
      },
      {
        id: 'location',
        header: t('location'),
        cell: (ctx) =>
          ctx.row.original?.location
            ? (BILL_LOCATION_LABELS[ctx.row.original.location as BillLocationValue] ??
              ctx.row.original.location)
            : '-',
        size: 140,
        enableSorting: false,
        sticky: 'left',
      },
      {
        id: 'bill_number',
        header: t('bill_number'),
        cell: (ctx) => ctx.row.original?.bill_number ?? "-",
        size: 160,
        enableSorting: false,
        sticky: 'left',
      },
      {
        id: 'username',
        header: t('username'),
        cell: (ctx) =>
          ctx.row.original?.customer_information?.name ?? "-",
        size: 160,
        enableSorting: false,
        sticky: 'left',
      },
      {
        id: 'bill_type',
        header: t('bill_type'),
        cell: (ctx) => <StatusBadge
          variance={ctx.row.original?.bill_information?.bill_type === "sell" ? "primary" : "danger"}
          children={ctx.row.original?.bill_information?.bill_type === "sell" ? "ขาย" : "ซื้อ"}
        />,
        size: 160,
        enableSorting: false,
      },
      {
        id: 'sale_mode',
        header: t('sale_mode'),
        cell: (ctx) => {
          const bi = ctx.row.original?.bill_information
          if (bi?.bill_type !== 'sell') return <span className="text-muted-foreground">—</span>
          const sm = bi?.sale_mode === 'wholesale' ? 'wholesale' : 'retail'
          return (
            <StatusBadge variance={sm === 'wholesale' ? 'cancel' : 'primary'}>
              {BILL_SALE_MODE_LABELS_TH[sm]}
            </StatusBadge>
          )
        },
        size: 100,
        enableSorting: false,
      },

      {
        id: 'total_cycle',
        header: t('total_cycle'),
        cell: (ctx) => ctx.row.original?.order_information?.reduce((acc, curr) => acc + curr.cycles.length, 0) ?? "-",
        size: 160,
        enableSorting: false,
      },
      {
        id: 'total_quantity',
        header: t('total_quantity'),
        cell: (ctx) => formatNumber(ctx.row.original?.order_information?.reduce((acc, curr) => acc + curr.cycles.reduce((acc, curr) => acc + curr.product_quantity, 0), 0) ?? 0),
        size: 160,
        enableSorting: false,
      },
      {
        id: 'total_price',
        header: t('total_price'),
        cell: (ctx) => formatNumber(ctx.row.original?.order_information?.reduce((acc, curr) => acc + curr.cycles.reduce((acc, curr) => acc + curr.product_quantity, 0) * curr.product_price, 0) ?? 0),
        size: 160,
        enableSorting: false,
      },
      {
        id: 'payment_method',
        header: t('payment_method'),
        cell: (ctx) => <StatusBadge
          variance={ctx.row.original?.payment_information?.payment_method === "cash" ? "primary" : "cancel"}
          children={ctx.row.original?.payment_information?.payment_method === "cash" ? "เงินสด" : "โอน"}
        />,
        size: 160,
        enableSorting: false,
      },
      {
        id: 'payment_status',
        header: t('payment_status'),
        cell: (ctx) => {
          const status = ctx.row.original?.payment_information?.payment_status
          const remaining =
            ctx.row.original?.remaining_amount ??
            ctx.row.original?.payment_information?.remaining_amount
          if (status === 'paid') {
            return <StatusBadge variance="primary">ชำระแล้ว</StatusBadge>
          }
          if (status === 'partial') {
            return (
              <StatusBadge variance="cancel">
                ชำระบางส่วน
                {remaining != null ? ` (ค้าง ${formatNumber(remaining)})` : ''}
              </StatusBadge>
            )
          }
          return <StatusBadge variance="cancel">ยังไม่ชำระ</StatusBadge>
        },
        size: 160,
        enableSorting: false,
      },
      {
        id: 'print_status',
        header: "สถานะพิมพ์",
        cell: (ctx) => <StatusBadge
          variance={ctx.row.original?.print_status === "printed" ? "primary" : "cancel"}
          children={ctx.row.original?.print_status === "printed" ? "พิมพ์แล้ว" : "ยังไม่พิมพ์"}
        />,
        size: 160,
        enableSorting: false,
      },
      {
        id: 'created_at',
        header: t('created_at'),
        cell: (ctx) => DateUtil.formatDate(ctx.row.original?.created_at ?? "-", DateUtil.DateFormat.DATE_TIME, locale),
        size: 160,
        enableSorting: true,
      },
      {
        id: 'updated_at',
        header: t('updated_at'),
        cell: (ctx) => DateUtil.formatDate(ctx.row.original?.updated_at ?? "-", DateUtil.DateFormat.DATE_TIME, locale),
        size: 160,
        enableSorting: true,
      },
      {
        id: 'created_by',
        header: t('created_by'),
        cell: (ctx) => ctx.row.original?.created_by?.name ?? "-",
        size: 160,
        enableSorting: false,
      },
      {
        id: 'updated_by',
        header: t('updated_by'),
        cell: (ctx) => ctx.row.original?.updated_by?.name ?? "-",
        size: 160,
        enableSorting: false,
      },
      {
        id: 'action',
        header: '',
        cell: (ctx) => (
          <BillRowActions onAction={(eventName) => onClickBillDropdownEvent(eventName, ctx.row.original)} />
        ),
        size: 112,
        minSize: 112,
        enableSorting: false,
        sticky: 'right',
      },
    ]
    return columnsRender
  }, [locale, t, search, onClickBillDropdownEvent])

  return { columns }
}
